import React, { useState, useEffect, useContext, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Select, FormGroup } from '../../components/Govuk';
import withAuthorisation from '../../components/auth/WithAuthorisation';
import { AuthContext } from '../../components/auth/AuthContext';
import { getRetailers, postFile, getSchema } from './api';
import { useMatomo } from '@datapunt/matomo-tracker-react';

const UPLOAD_TIMEOUT_MS = 240000; // 4 minutes

const FileUpload = (props) => {
  const [file, setFile] = useState(null);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [retailerChoice, setRetailerChoice] = useState('');
  const [uploadError, setUploadError] = useState({
    serverError: false,
    virusError: false,
    fileEmptyError: false,
    fileTypeError: false,
    fileSizeError: false,
    fileSizeZeroError: false,
    fileNotMappedError: false,
  });
  const { trackPageView, trackEvent } = useMatomo();
  const { isDeepSupportOrHomeOffice } = useContext(AuthContext);
  const [disabled, setDisabled] = useState(false);

  // Strict in-flight guard + idempotency
  const [inFlightId, setInFlightId] = useState(null);
  const abortRef = useRef(null);

  const mainWrapperError = 'govuk-form-group govuk-form-group--error';
  const mainWrapperNoError = 'govuk-form-group';
  const spanErrorClass = 'govuk-error-message';

  useEffect(() => {
    setUploadError({
      serverError: false,
      virusError: false,
      fileEmptyError: false,
      fileTypeError: false,
      fileSizeError: false,
      fileSizeZeroError: false,
      fileNotMappedError: false,
    });

    trackPageView({
      documentTitle: 'File Upload - Report Suspicious Chemical Activity '
    });
    trackEvent({
      category: 'File Upload Page',
      action: 'Visited',
    });
  }, [file, trackEvent, trackPageView]);

  useEffect(() => {
    if (isDeepSupportOrHomeOffice()) {
      getRetailers()
        .then((res) => {
          const options = [];
          options.push({ value: '', description: '' });
          res.data.forEach((retailer) => {
            options.push({ value: retailer, description: retailer });
          });
          options.sort();
          setRetailerOptions(options);
        })
        .catch(() => {
          props.history.push('/sar-error');
        });
    }
    // cleanup: abort any in-flight request if component unmounts
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const isBadFileResponse = (response) => {
    return (
      response.status === 400 &&
      response.data.includes('Unsuccessful save of incoming retailer data')
    );
  };

  const isVirusResponse = (response) => {
    return response.status === 400 && response.data.includes('virus');
  };

  const isInvalidFileTypeResponse = (response) => {
    return response.status === 415 && response.data.includes('type');
  };

  const isFileSizeErrorResponse = (response) => {
    return response.status === 413;
  };

  const isFileSizeZeroErrorResponse = (response) => {
    return response.status === 422;
  };

  const handleChange = (fileList) => {
    if (fileList) {
      const inputFile = fileList[0];
      setFile(inputFile);
    }
  };

  // Safe UUID (fallback if needed)
  const generateIdempotencyKey = () => {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return 'idemp-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  };

  const uploadFile = () => {
    // Hard guard against double-clicks or re-entrance
    if (inFlightId) {
      return;
    }

    setDisabled(true);

    if (!file) {
      setUploadError({ fileEmptyError: true });
      setDisabled(false);
      return;
    }

    setUploadError({ fileEmptyError: false });

    const idempotencyKey = generateIdempotencyKey();
    setInFlightId(idempotencyKey);

    // AbortController to cancel if needed and prevent stray retries
    const controller = new AbortController();
    abortRef.current = controller;

    const headers = retailerChoice
      ? {
          accept: 'application/json',
          'content-type': 'multipart/form-data',
          retailer: retailerChoice,
          'X-Idempotency-Key': idempotencyKey, // let backend dedupe
        }
      : {
          accept: 'application/json',
          'content-type': 'multipart/form-data',
          'X-Idempotency-Key': idempotencyKey,
        };

    const config = {
      headers,
      timeout: UPLOAD_TIMEOUT_MS,            // extend to 4 minutes
      signal: controller.signal,             // allow abort on unmount
      maxBodyLength: Infinity,               // ensure large files aren’t blocked by client
      maxContentLength: Infinity,
    };

    const formData = new FormData();
    formData.append('file', file);

    postFile(formData, config)
      .then((response) =>  {
        // clear in-flight state
        setInFlightId(null);
        abortRef.current = null;

        if (response.data.invalid_data.includes('No Upload Error')) {
          const responseUuid = response.data.guid;
          props.setUuid(responseUuid);
          props.history.push('/success');
        } else {
          const responseUuid = response.data.guid;
          const responseInvalidData = response.data.invalid_data;
          props.setUuid(responseUuid);
          props.setInvalidData(responseInvalidData);
          props.history.push('/success-invalid-data');
        }
        trackEvent({
          category: 'File Upload',
          action: 'Successful',
        });
      })
      .catch((error) => {
        setDisabled(false);
        setInFlightId(null);
        abortRef.current = null;

        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return;
        }

        if (error.response) {
          if (isBadFileResponse(error.response)) {
            setUploadError({ fileNotMappedError: true });
          }
          if (isVirusResponse(error.response)) {
            setUploadError({ virusError: true });
          }
          if (isInvalidFileTypeResponse(error.response)) {
            setUploadError({ fileTypeError: true });
          }
          if (isFileSizeErrorResponse(error.response)) {
            setUploadError({ fileSizeError: true });
          }
          if (isFileSizeZeroErrorResponse(error.response)) {
            setUploadError({ fileSizeZeroError: true });
          }
          trackEvent({
            category: 'File Upload',
            action: 'Failed',
          });
        } else {
          // Network / timeout / proxy close, surface a generic server error
          setUploadError({ serverError: true });
          trackEvent({
            category: 'File Upload',
            action: 'Failed',
          });
        }
      });
  };

  const getWrapperClass = () => {
    let className = mainWrapperNoError;
    if (
      uploadError.fileEmptyError ||
      uploadError.virusError ||
      uploadError.fileTypeError ||
      uploadError.fileSizeError ||
      uploadError.fileSizeZeroError ||
      uploadError.fileNotMappedError
    ) {
      className = mainWrapperError;
    }
    return className;
  };

  const getSpanClass = () => {
    let className = '';
    if (
      uploadError.fileEmptyError ||
      uploadError.virusError ||
      uploadError.fileTypeError ||
      uploadError.fileSizeError ||
      uploadError.fileSizeZeroError ||
      uploadError.fileNotMappedError
    ) {
      className = spanErrorClass;
    }
    return className;
  };

  const getErrorMessage = () => {
    if (uploadError.fileEmptyError) {
      return (
        <>
          <form action="/form-handler" method="post" novalidate>
            <div class="govuk-error-summary" data-module="govuk-error-summary">
              <div role="alert"></div>
              <h2 class="govuk-error-summary__title">There is a problem</h2>
              <div class="govuk-error-summary__body">
                <ul class="govuk-list govuk-error-summary__list">
                  <li>
                    <a href="#behalf-of-retailer-select">Select a report</a>
                  </li>
                </ul>
              </div>
            </div>
          </form>
        </>
      );
    } else if (uploadError.virusError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>The selected file
          contains a virus
        </>
      );
    } else if (uploadError.fileTypeError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>
          The selected file must be a JSON, CSV, XLS or XLSX
        </>
      );
    } else if (uploadError.fileSizeError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>The selected file
          must be smaller than 20MB
        </>
      );
    } else if (uploadError.fileSizeZeroError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>The selected file
          must not be empty.
        </>
      );
    } else if (uploadError.fileNotMappedError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>The file contents
          is in an unknown schema format. This means the columns or fields are
          unrecognised. Please contact{' '}
          <a href="mailto:ChemicalReportingTeam@homeoffice.gov.uk">
            ChemicalReportingTeam@homeoffice.gov.uk
          </a>{' '}
          to rectify this issue.
        </>
      );
    } else if (uploadError.serverError) {
      return (
        <>
          <span className="govuk-visually-hidden">Error:</span>
          The upload could not be completed due to a network or server timeout. Please try again.
        </>
      );
    }
    return null;
  };

  const getFileUploadClass = () => {
    let className = 'govuk-file-upload';
    if (
      uploadError.fileEmptyError ||
      uploadError.virusError ||
      uploadError.fileTypeError ||
      uploadError.fileSizeError ||
      uploadError.fileSizeZeroError ||
      uploadError.fileNotMappedError
    ) {
      className = className + ' govuk-file-upload--error';
    }
    return className;
  };

  const handleRetailerChange = (retailer) => {
    setRetailerChoice(retailer);
  };

  const downloadSchema = (type) => {
    getSchema(type).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      // set the file type for CSV as XLSX as this is the actual download type
      let fileName = '';
      switch (type) {
        case 'json':
          fileName = 'SAR_schema.json';
          break;
        case 'xml':
          fileName = 'SAR_schema.xml';
          break;
        case 'csv':
          fileName = 'SAR_schema_guidance.xlsx';
          break;
        case 'csv-header':
          fileName = 'SAR_schema.csv';
          break;
        default:
          fileName = 'schema';
      }
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      trackEvent({
        category: `${type} Schema`,
        action: 'Downloaded',
      });
    });
  };

  return (
    <div>
      <main className="govuk-main-wrapper" id="content" role="main">
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-two-thirds">
            <FormGroup>
              <div className={getWrapperClass()}>
                {uploadError.fileEmptyError ||
                uploadError.virusError ||
                uploadError.fileTypeError ||
                uploadError.fileSizeZeroError ||
                uploadError.fileSizeError ||
                uploadError.fileNotMappedError ||
                uploadError.serverError ? (
                  <span
                    id="file-upload-1-error"
                    data-test="file-upload-error"
                    className={getSpanClass()}
                  >
                    {getErrorMessage()}
                  </span>
                ) : (
                  <span id="file-upload-1-error" style={{ display: 'none' }} /> //to fulfil aria described-by reference
                )}
              </div>
            </FormGroup>
            <h1 className="govuk-heading-l" tabIndex="0" data-test="upload-header">
              Upload data
            </h1>
            <p className="govuk-body" tabIndex="0" data-test="upload-message-1">
              The service may not be able to process your data if it is not
              submitted correctly. The guidance document below explains how to
              submit data via JSON, CSV, XLS and XLSX formats.
            </p>
            <ul className="govuk-list govuk-list--bullet">
              <li>
                <a
                  href="#"
                  className="govuk-link"
                  data-test={'csv-schema-link'}
                  onClick={() => downloadSchema('csv')}
                >
                  Guidance for submitting data
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="govuk-link"
                  data-test={'json-schema-link'}
                  onClick={() => downloadSchema('json')}
                >
                  Download JSON schema
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="govuk-link"
                  data-test={'csv-header-link'}
                  onClick={() => downloadSchema('csv-header')}
                >
                  Download CSV schema
                </a>
              </li>
            </ul>

            <h2 className="govuk-heading-s" tabIndex="0">Upload data</h2>
            {isDeepSupportOrHomeOffice() && (
              <FormGroup>
                <Select
                  label={'Upload SAR on behalf of a retailer:'}
                  name="behalf-of-retailer-select"
                  id="behalf-of-retailer-select"
                  onChange={handleRetailerChange}
                  options={retailerOptions}
                  selected={retailerChoice}
                  dataTest="behalf-of-retailer-select"
                />
              </FormGroup>
            )}
            <FormGroup>
              <div className={getWrapperClass()}>
                {uploadError.fileEmptyError ||
                uploadError.virusError ||
                uploadError.fileTypeError ||
                uploadError.fileSizeZeroError ||
                uploadError.fileSizeError ||
                uploadError.fileNotMappedError ||
                uploadError.serverError ? (
                  <span
                    id="file-upload-1-error"
                    data-test="file-upload-error"
                    className={getSpanClass()}
                  >
                    {getErrorMessage()}
                  </span>
                ) : (
                  <span id="file-upload-1-error" style={{ display: 'none' }} /> //to fulfil aria described-by reference
                )}

                <label style={{ display: 'none' }} htmlFor="file-upload-1">
                  Upload file
                </label>
                <input
                  onChange={(e) => handleChange(e.target.files)}
                  className={getFileUploadClass()}
                  id="file-upload-1"
                  name="file-upload-1"
                  type="file"
                  aria-label="Choose file"
                />
              </div>
            </FormGroup>
          </div>

          <div className="govuk-grid-column-two-thirds">
            <Button
              content={disabled ? 'Loading..' : 'Upload'}
              action={uploadFile}
              disabled={disabled || !!inFlightId}
            />
            <h2 className="govuk-heading-s" tabIndex="0">Need help?</h2>
            <p className="govuk-body" tabIndex="0" data-test="upload-contact-1">
              For any issues, email the team at{' '}
              <a
                href="mailto:ChemicalReportingTeam@homeoffice.gov.uk"
                data-test="upload-contact-1-link"
              >
                ChemicalReportingTeam@homeoffice.gov.uk
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

const FileUploadWithAuth = withAuthorisation({ forwardUrl: '/upload' })(
  withRouter(FileUpload)
);

export { FileUploadWithAuth as FileUpload };
